import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../hooks/useChat';
import useUsageLimits from '../hooks/useUsageLimits';
import { Send, Trash2, Sparkles, Loader, Lock, X, Gift, BookOpen } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import UpgradeModal from '../components/UpgradeModal';

const ChatPage = () => {
  const { profile, isAuthenticated } = useAuth();
  const { canUse, getLimit, isPro } = useUsageLimits();
  const { messages, isLoading, sendMessage, clearChat, loadChatHistory } = useChat();
  const [input, setInput] = useState('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showEbookPopup, setShowEbookPopup] = useState(false);
  const messagesEndRef = useRef(null);

  const chatLimit = getLimit('chat');
  const canSendMessage = canUse('chat');

  // Popup e-book après 30s pour les non-inscrits
  useEffect(() => {
    if (isAuthenticated) return;
    
    // Vérifier si déjà affiché cette session
    const alreadyShown = sessionStorage.getItem('ebook_popup_shown');
    if (alreadyShown) return;

    const timer = setTimeout(() => {
      setShowEbookPopup(true);
      sessionStorage.setItem('ebook_popup_shown', 'true');
    }, 30000); // 30 secondes

    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  const handleEbookClaim = () => {
    setShowEbookPopup(false);
    setShowAuthModal(true);
  };

  // Charger l'historique si connecté
  useEffect(() => {
    if (isAuthenticated) {
      loadChatHistory();
    }
  }, [isAuthenticated, loadChatHistory]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Vérifier si peut envoyer
    if (!canSendMessage) {
      if (!isAuthenticated) {
        setShowAuthModal(true);
      } else {
        setShowUpgradeModal(true);
      }
      return;
    }

    const text = input;
    setInput('');
    await sendMessage(text);
  };

  const handleQuickAction = async (text) => {
    if (!canSendMessage) {
      if (!isAuthenticated) {
        setShowAuthModal(true);
      } else {
        setShowUpgradeModal(true);
      }
      return;
    }
    await sendMessage(text);
  };

  const quickActions = [
    { icon: '🥗', label: 'Une idée de repas', text: 'Bonjour Louis, pouvez-vous me suggérer une recette équilibrée pour ce soir ?' },
    { icon: '📊', label: 'Mes besoins caloriques', text: 'Bonjour Louis, j\'aimerais comprendre mes besoins caloriques journaliers' },
    { icon: '🍎', label: 'Snacks sains', text: 'Louis, quels snacks me conseillez-vous pour éviter les fringales ?' },
    { icon: '💪', label: 'Conseils motivation', text: 'J\'ai du mal à rester motivé(e), avez-vous des conseils Louis ?' }
  ];

  const formatMessage = (content) => {
    // Nettoyer le markdown et formater proprement
    return content.split('\n').map((line, i) => {
      // Enlever les ### ## etc
      let cleanLine = line
        .replace(/^#{1,4}\s*/g, '') // Enlever # ## ### ####
        .replace(/\*\*/g, '')       // Enlever **
        .replace(/\*/g, '')         // Enlever *
        .replace(/__/g, '')         // Enlever __
        .replace(/_/g, ' ')         // Remplacer _ par espace
        .trim();
      
      // Listes avec tirets ou points
      if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
        return <div key={i} className="msg-list-item">→ {cleanLine.slice(2)}</div>;
      }
      
      // Listes numérotées
      const numMatch = line.match(/^(\d+)\.\s/);
      if (numMatch) {
        return <div key={i} className="msg-list-item">{numMatch[1]}. {cleanLine.slice(numMatch[0].length)}</div>;
      }
      
      // Ligne vide
      if (!cleanLine) {
        return <br key={i} />;
      }
      
      // Texte normal
      return <p key={i} className="msg-para">{cleanLine}</p>;
    });
  };

  const isBlocked = !canSendMessage;
  const remainingMessages = chatLimit.isUnlimited ? '∞' : chatLimit.remaining;

  return (
    <div className="chat-page">
      {messages.length === 0 ? (
        <div className="chat-welcome">
          <div className="welcome-hero">
            <div className="louis-intro">
              <img src="./robot-icon.svg" alt="Louis" className="louis-avatar-large" />
              <div className="louis-badge">Coach nutrition</div>
            </div>
            <h1>
              {isAuthenticated && profile?.name 
                ? <>Bonjour {profile.name} !</> 
                : <>Bonjour, je suis <span>Louis</span></>}
            </h1>
            <p className="welcome-subtitle">
              {isAuthenticated 
                ? 'Je suis là pour vous accompagner dans votre parcours nutrition. Comment puis-je vous aider ?'
                : 'Votre coach nutrition personnel. Je vous aide à atteindre vos objectifs de manière simple et adaptée à votre vie.'}
            </p>
          </div>

          <div className="quick-actions-grid">
            {quickActions.map((action, i) => (
              <button
                key={i}
                className="quick-action-card"
                onClick={() => handleQuickAction(action.text)}
                disabled={isBlocked}
              >
                <span className="quick-icon">{action.icon}</span>
                <span className="quick-label">{action.label}</span>
              </button>
            ))}
          </div>

          <div className="motivation-banner">
            <Sparkles size={20} />
            <p>
              <strong>Chaque petit pas compte.</strong> Posez-moi votre première question pour commencer !
            </p>
          </div>

          {!isAuthenticated && (
            <div className="trial-notice">
              <span className="trial-badge">✨ {remainingMessages} messages gratuits</span>
              <span>Inscrivez-vous pour en avoir 20/mois</span>
            </div>
          )}

          {isAuthenticated && !isPro && (
            <div className="trial-notice">
              <span className="trial-badge">💬 {remainingMessages} messages restants</span>
              <span>Passez en Pro pour un accès illimité</span>
            </div>
          )}

          {isAuthenticated && isPro && (
            <div className="trial-notice">
              <span className="trial-badge" style={{ background: '#10b981' }}>👑 Pro</span>
              <span>Messages illimités • Merci pour votre confiance !</span>
            </div>
          )}
        </div>
      ) : (
        <div className="chat-active">
          <div className="chat-header-bar">
            <div className="chat-header-louis">
              <img src="./robot-icon.svg" alt="Louis" className="louis-avatar-small" />
              <div>
                <h3>Louis</h3>
                <span className="louis-status">🟢 En ligne</span>
              </div>
            </div>
            <button onClick={clearChat} className="clear-btn">
              <Trash2 size={16} />
              Nouveau chat
            </button>
          </div>

          <div className="messages-list">
            {messages.map((msg) => (
              <div key={msg.id} className={`message ${msg.role}`}>
                <div className="message-avatar">
                  {msg.role === 'assistant' 
                    ? <img src="./robot-icon.svg" alt="Louis" className="bot-avatar" />
                    : (profile?.name?.[0]?.toUpperCase() || '👤')}
                </div>
                <div className="message-content">
                  {msg.role === 'assistant' && <span className="message-sender">Louis</span>}
                  <div className="message-bubble">
                    {msg.role === 'assistant' ? formatMessage(msg.content) : msg.content}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="message assistant">
                <div className="message-avatar">
                  <img src="./robot-icon.svg" alt="Louis" className="bot-avatar" />
                </div>
                <div className="message-content">
                  <span className="message-sender">Louis</span>
                  <div className="message-bubble typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Blocked message */}
          {isBlocked && (
            <div className="blocked-banner">
              <Lock size={18} />
              <span>
                {!isAuthenticated 
                  ? 'Inscris-toi gratuitement pour continuer à discuter avec Louis !'
                  : 'Tu as atteint la limite. Passe en Pro pour continuer !'}
              </span>
              <button onClick={() => !isAuthenticated ? setShowAuthModal(true) : setShowUpgradeModal(true)}>
                {!isAuthenticated ? 'S\'inscrire' : 'Passer Pro'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="chat-input-area">
        <form onSubmit={handleSubmit} className="input-form">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isBlocked 
              ? (!isAuthenticated ? '🔒 Inscris-toi pour continuer...' : '🔒 Passe Pro pour continuer...')
              : 'Écris à Louis... 💬'
            }
            className={`chat-input ${isBlocked ? 'blocked' : ''}`}
            disabled={isLoading || isBlocked}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!input.trim() || isLoading || isBlocked}
          >
            {isLoading ? <Loader size={20} className="spin" /> : <Send size={20} />}
          </button>
        </form>
      </div>

      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} />
      )}

      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}

      {/* Popup E-book gratuit */}
      {showEbookPopup && (
        <div className="ebook-popup-overlay" onClick={() => setShowEbookPopup(false)}>
          <div className="ebook-popup" onClick={e => e.stopPropagation()}>
            <button className="ebook-close" onClick={() => setShowEbookPopup(false)}>
              <X size={20} />
            </button>
            
            <div className="ebook-gift-icon">
              <Gift size={32} />
            </div>
            
            <h2>🎁 Votre cadeau de bienvenue</h2>
            
            <div className="ebook-preview">
              <BookOpen size={48} />
              <span>GUIDE OFFERT</span>
            </div>
            
            <h3>Votre Plan Nutrition Personnalisé</h3>
            <p>Menu 7 jours + liste de courses adapté à <strong>VOS objectifs</strong></p>
            
            <ul className="ebook-benefits">
              <li>✓ Créé par Louis selon vos préférences</li>
              <li>✓ Recettes faciles et équilibrées</li>
              <li>✓ Liste de courses prête à imprimer</li>
              <li>✓ 100% gratuit, sans engagement</li>
            </ul>
            
            <button className="ebook-cta" onClick={handleEbookClaim}>
              <Sparkles size={20} />
              Recevoir mon e-book gratuit
            </button>
            
            <p className="ebook-footer">Inscription gratuite en 30 secondes</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
